export type SocketMessage<T = any> = {
    type: string;
    payload: T
}

export type ConnectionState = 
'idle' |
'connecting' |
'open' |
'reconnecting' |
'disconnected' |
'error'

type CallbackEvent<T = any> = (data: T) => void

export default class WebsocketService {
    private socket: WebSocket | null = null;
    private url: string;

    private listeners = new Map<string, Set<CallbackEvent>>();

    private connectionState: ConnectionState = 'idle'
    private connectionListeners = new Set<(state: ConnectionState) => void>()

    private messageQueue: string[] = [];

    private reconnectAttempts: number = 0;
    private manuallyClosed: boolean = false;

    private heartbeatInterval: ReturnType<typeof setInterval> | null = null;
    private reconnectTimeout: ReturnType<typeof setTimeout> | null = null;

    constructor(url: string) {
        this.url = url
    }

    connect() {

        if(this.socket && (this.socket.OPEN || this.socket.CONNECTING)) {
            return
        }

        this.setConnectionState('connecting')

        this.manuallyClosed = false;

        this.socket = new WebSocket(this.url)

        this.socket.addEventListener('open', this.onOpen)
        this.socket.addEventListener('message', this.onMessage)
        this.socket.addEventListener('error', this.onError)
        this.socket.addEventListener('close', this.onClose)


    }

    private setConnectionState(state: ConnectionState) {
        this.connectionState = state

        this.connectionListeners.forEach(cb => cb(state))
    }

    private onOpen = () =>  {
        this.reconnectAttempts = 0

        this.flushQueue()
        this.startHeartbeat()
        
        const token = localStorage.getItem('accessToken')

        if(token) {
            this.send({
                type: "connect_user",
                payload: {
                    accessToken: token
                }
            })
        }
        this.setConnectionState('open')
    }

    private onMessage = (event: MessageEvent) => {
        try {
            const message: SocketMessage = JSON.parse(event.data)

            const subs = this.listeners.get(message.type)
            subs?.forEach(cb => cb(message.payload))
        } catch (error) {
            console.log("WS error parse:" + error)
        }
    }

    private onClose = () => {
        this.stopHeartbeat()
        this.setConnectionState('disconnected')
        if(!this.manuallyClosed) {
            this.reconnect()
        }
    }

    private onError = (event: Event) => {
        this.setConnectionState('error')
    }

    private send(data: SocketMessage) {
        const raw = JSON.stringify(data)

        if(this.socket?.readyState === WebSocket.OPEN) {
            this.socket.send(raw)
        }
        else {
            this.messageQueue.push(raw)
        }
    }

    private flushQueue() {
        while(this.messageQueue.length) {
            const msg = this.messageQueue.shift()
            if(msg && this.socket?.readyState === WebSocket.OPEN) {
                this.socket.send(msg)
            }
        }
    }

    private reconnect() {
        this.reconnectAttempts++;

        const delay = Math.min(1000 * 2 ** this.reconnectAttempts, 30000)
        this.setConnectionState('reconnecting')
        this.reconnectTimeout = setTimeout(() => {
            this.connect()
        }, delay)
    }

    private startHeartbeat() {
        this.heartbeatInterval = setInterval(() => {
            this.send({type: 'ping', payload: null})
        }, 30000)
    }

    private stopHeartbeat() {
        if(this.heartbeatInterval) {
            clearInterval(this.heartbeatInterval)
            this.heartbeatInterval = null;
        }
    }

    subscribe<T = any>(event: string, cb: CallbackEvent<T>) {
        if(!this.listeners.has(event)) {
            this.listeners.set(event, new Set())
        }
        this.listeners.get(event)!.add(cb)

        return () => this.unsubscribe(event, cb)
    }

    unsubscribe<T = any>(event: string, cb: CallbackEvent<T>) {
        this.listeners.get(event)?.delete(cb)
    }

    subscribeState(callback: (state: ConnectionState) => void) {
        this.connectionListeners.add(callback)

        callback(this.connectionState)

        return () => {this.connectionListeners.delete(callback); return;}
    }

    disconnect() {
        this.manuallyClosed = true;
        this.stopHeartbeat()
        if(this.reconnectTimeout) {
            clearTimeout(this.reconnectTimeout)

        }
        this.socket?.close()
        this.socket = null

        this.listeners.clear()
        this.messageQueue = []
    }

}